/**
 * NextGameNudge.jsx
 *
 * End-of-game "keep playing" block. Every daily is free, but a first-time
 * player finishing one game has no idea the others exist, so the results
 * screens all end with the same nudge: a NEXT GAME button pointed at the top
 * unplayed daily, plus the rest listed underneath.
 *
 * Shown win or lose, signed in or out. Signing up stays available as a
 * secondary option rather than the only thing on the screen.
 *
 * Games already finished today are shown greyed with a check so the list
 * doubles as a "what's left today" scoreboard. NBA and Mish Mosh require an
 * account, so for guests they route to the auth modal instead of the game.
 */

import React from 'react';
import {
  hasPlayedToday,
  hasPlayedNBAToday,
  hasPlayedMoshToday,
  hasPlayedNTGToday,
  hasPlayedNTGSrToday,
  hasPlayedDigitsToday,
} from '../utils/dailyChallenge';

// Home-screen order, so the list reads the same in both places.
const GAMES = [
  { key: 'nfl', label: 'NFL Daily', blurb: '5 questions', guestOk: true, played: hasPlayedToday },
  { key: 'nba', label: 'NBA Daily', blurb: '5 questions', guestOk: false, played: hasPlayedNBAToday },
  { key: 'mosh', label: 'Mish Mosh Daily', blurb: 'MLB, NHL, celebs, more', guestOk: false, played: hasPlayedMoshToday },
  { key: 'sr', label: 'Name That Guy SR.', blurb: '1 guy, 3 guesses', guestOk: true, played: hasPlayedNTGSrToday },
  { key: 'jr', label: 'Name That Guy JR.', blurb: '1 question, 10 seconds', guestOk: true, played: hasPlayedNTGToday },
  { key: 'digits', label: 'Digits', blurb: 'which guy wore the number', guestOk: false, played: hasPlayedDigitsToday },
];

const NextGameNudge = ({ currentGame, currentUser, onPlayDaily, onShowAuth }) => {
  if (!onPlayDaily) return null;

  const others = GAMES.filter((g) => g.key !== currentGame).map((g) => {
    let played = false;
    try { played = !!g.played(); } catch { played = false; }
    return { ...g, played, locked: !g.guestOk && !currentUser };
  });

  // Best next click: unplayed and actually playable right now.
  const next = others.find((g) => !g.played && !g.locked);
  const rest = others.filter((g) => g !== next);
  if (!next && rest.every((g) => g.played)) return null; // everything done today

  return (
    <div style={{ background: 'var(--bg-navy)', border: '1px solid var(--border-dark)', borderRadius: 'var(--radius)', padding: '12px', marginTop: '12px', textAlign: 'center' }}>
      <p style={{ fontFamily: 'var(--font-heading)', fontSize: '12px', color: 'var(--gold)', letterSpacing: '2px', marginBottom: '8px' }}>
        MORE DAILY CHALLENGES
      </p>

      {next && (
        <button
          className="btn btn-primary"
          style={{ marginTop: '0', marginBottom: rest.length ? '10px' : '0' }}
          onClick={() => onPlayDaily(next.key)}
        >
          NEXT GAME: {next.label} &rarr;
        </button>
      )}

      {rest.map((g) => (
        <button
          key={g.key}
          onClick={() => (g.locked ? onShowAuth?.() : onPlayDaily(g.key))}
          disabled={g.played}
          style={{
            display: 'block', width: '100%', margin: '4px 0', padding: '7px 10px',
            background: 'none', border: '1px solid var(--border-dark)', borderRadius: 'var(--radius-sm)',
            cursor: g.played ? 'default' : 'pointer', textAlign: 'left',
            fontFamily: 'var(--font-body)', fontSize: '12px',
            color: g.played ? 'var(--text-dim)' : 'var(--text-white)',
            opacity: g.played ? 0.55 : 1,
          }}
        >
          {g.played ? '✓ ' : ''}{g.label}
          <span style={{ color: 'var(--text-dim)', fontSize: '11px' }}>
            {g.played ? ' · played today' : g.locked ? ' · sign in to unlock' : ` · ${g.blurb}`}
          </span>
          {!g.played && (
            <span style={{ float: 'right', fontStyle: 'italic', fontSize: '9px', color: 'rgba(201,168,76,0.55)', letterSpacing: '0.5px', marginTop: '2px' }}>click</span>
          )}
        </button>
      ))}
    </div>
  );
};

export default NextGameNudge;
